import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const EMPTY = { items: [], strokes: [] };

/**
 * One board per question, over a single canvas.
 *
 * Annotations belong to the question they were drawn beside — ink left
 * against question 1 has nothing to say on question 2 — but the paper shows
 * one question at a time, so there is one board and it is swapped as the
 * question changes: what was on it is put away under the question being
 * left, and whatever was stored for the question being opened is put out.
 *
 * The whole set is what gets saved, so moving between questions never loses
 * what was drawn on the one you left.
 */
function useQuestionBoard(board, questionNumber) {
  const [saved, setSaved] = useState({});
  const showing = useRef(null);

  // The board as it is right now, for the operations below to read without
  // taking it as a dependency and re-running on every stroke.
  const live = useRef({ items: board.items, strokes: board.strokes });
  live.current = { items: board.items, strokes: board.strokes };

  useEffect(() => {
    if (questionNumber == null || showing.current === questionNumber) return;

    const leaving = showing.current;
    showing.current = questionNumber;

    setSaved((prev) => {
      const next = leaving == null ? prev : { ...prev, [leaving]: live.current };
      const opening = next[questionNumber] || EMPTY;
      // Put the new question's board out after this state settles, so the
      // swap is one visual step rather than a flash of the old ink.
      board.replaceAll(opening.items, opening.strokes);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionNumber]);

  /** Everything drawn on this paper, with the open question up to date. */
  const document = useMemo(
    () =>
      questionNumber == null
        ? saved
        : { ...saved, [questionNumber]: { items: board.items, strokes: board.strokes } },
    [saved, questionNumber, board.items, board.strokes]
  );

  /** Replace the lot — what loading from the server does. */
  const load = useCallback(
    (stored) => {
      const next = stored && typeof stored === "object" ? stored : {};
      setSaved(next);
      if (showing.current != null) {
        const opening = next[showing.current] || EMPTY;
        board.replaceAll(opening.items, opening.strokes);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return { document, load };
}

/**
 * The two boards a paper has, kept per question and saved together.
 *
 * `frame` is drawn on the paper and scrolls with the question; `page` is the
 * dotted ground around it and stays put. They are separate boards because
 * they move differently, but they are one piece of work, so they go to the
 * server as one document: `{ frame: {[question]: board}, page: {...} }`.
 *
 * Annotations saved before the page could be drawn on were only ever on the
 * paper, stored as `{[question]: board}`. That shape is read as the frame.
 */
export function usePaperBoards({ frame, page, questionNumber }) {
  const onFrame = useQuestionBoard(frame, questionNumber);
  const onPage = useQuestionBoard(page, questionNumber);

  const document = useMemo(
    () => ({ frame: onFrame.document, page: onPage.document }),
    [onFrame.document, onPage.document]
  );

  const loadFrame = onFrame.load;
  const loadPage = onPage.load;
  const load = useCallback(
    (stored) => {
      const doc = stored && typeof stored === "object" ? stored : {};
      const layered = "frame" in doc || "page" in doc;
      loadFrame(layered ? doc.frame : doc);
      loadPage(layered ? doc.page : {});
    },
    [loadFrame, loadPage]
  );

  return { document, load };
}
